import { useEffect, useState } from 'react'

const canMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'

const getMatches = (query: string | undefined) =>
  query && canMatchMedia() ? window.matchMedia(query).matches : false

/**
 * Tracks whether a CSS media query currently matches. Pass `undefined` to opt
 * out — nothing is subscribed and the result is always `false`.
 *
 * The initial value is read synchronously so the first render already reflects
 * the real viewport — components using this never flash the wrong state.
 */
export function useMediaQuery(query: string | undefined) {
  const [matches, setMatches] = useState(() => getMatches(query))

  useEffect(() => {
    if (!query || !canMatchMedia()) {
      setMatches(false)
      return
    }

    const mediaQueryList = window.matchMedia(query)
    // Re-sync in case the viewport changed between the initial render and this effect
    setMatches(mediaQueryList.matches)

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    mediaQueryList.addEventListener('change', onChange)

    return () => mediaQueryList.removeEventListener('change', onChange)
  }, [query])

  return matches
}
